

interface EventRegistrySingletonValue {
  eventMapByKeyAndSubject: Map<string, Map<string, () => void>>,
}

/**
 * The only instance of our Singleton
 */
let instance: ReturnType<typeof makeEventRegistrySingleton<EventRegistrySingletonValue>>;

/**
 * Singleton supplies accessors using Revealing Module 
 * pattern and we use generics, since we could reuse 
 * this across multiple singletons
 *
 * Note: Object.freeze() not required due to type narrowing!
 */
const makeEventRegistrySingleton = <T>(initial?: T) => {
  /** Closure of the singleton's value to keep it private */
  let _value: T | undefined = initial;

  /** Only the accessors are returned */
  return {
    getValue: (): T | undefined => _value,
    setValue: (value: T) => _value = value,
  };
};

/**
 * Retrieves the only instance of the Singleton
 * and allows a once-only initialisation
 * (additional changes require the setValue accessor)
 */
const getInstance = (initial?: EventRegistrySingletonValue) => {
  if (!instance) {
    instance = makeEventRegistrySingleton<EventRegistrySingletonValue>(initial);
    return instance;
  }
  if (initial) {
    throw Error("Singleton already initialised")
  }
  return instance;
};

const initialize = () => {
  if (getInstance().getValue() === undefined) {
    getInstance().setValue(
      {
        eventMapByKeyAndSubject: new Map<string, Map<string, () => void>>(),
      })
  }
}

const getEventsMapByKeyName = (key: string): Map<string, () => void> | undefined => {
  return getInstance().getValue()?.eventMapByKeyAndSubject?.get(key);
}

const registerEvent = (key: string, subject: string, event: () => void) => {
  initialize();
  const instanceMap = getInstance().getValue()?.eventMapByKeyAndSubject;
  if (!instanceMap) throw Error(`Event Map not available`)
  if (instanceMap.has(key)) {
    const keyMap = instanceMap.get(key)
    if (keyMap?.get(subject)) {
      //console.log(instanceMap, `For the event, key and subject already definded: ${key + " " + subject}`)
      keyMap?.set(subject, event)
      return;
      //throw Error(`For the event, key and subject already definded: ${key + " " + subject}`)
    }
    else {
      keyMap?.set(subject, event)
    }
  } else {
    const keyMap = instanceMap.set(key, new Map()).get(key)
    keyMap?.set(subject, event)
  }
}

const removeEvent = (key: string, subject: string, event: () => void) => {
  return getInstance().getValue()?.eventMapByKeyAndSubject.get(key)?.delete(subject);
}

export {
  getEventsMapByKeyName,
  registerEvent,
  removeEvent,
}